import { supabase } from './supabase';

/**
 * Гарантирует, что перед записью в БД у клиента есть живая авторизованная сессия.
 *
 * Зачем: у нового пользователя сессия устанавливается через tg-auth в момент входа,
 * и иногда (тайминг / потеря localStorage в Telegram WebView / неудачный авто-рефреш)
 * запрос уходит анонимом → Postgres отвечает "permission denied for table profiles".
 * Этот хелпер закрывает весь класс проблемы: проверяет и при необходимости
 * восстанавливает сессию ДО записи.
 *
 * Цепочка восстановления:
 *   1) текущая сессия (getSession)
 *   2) обновление по refresh-токену (refreshSession)
 *   3) переавторизация через Edge Function tg-auth по initData (Telegram Mini App)
 *
 * @returns true — сессия есть и запись безопасна; false — восстановить не удалось.
 */
export async function ensureSession(force = false): Promise<boolean> {
  // 1) Уже есть валидная сессия?
  // force=true пропускает эту проверку: нужно, когда сервер ОТВЕРГ наш токен (401).
  // Тогда сессия формально «есть», но протухла, и верить ей нельзя.
  if (!force) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) return true;
  }

  // 2) Пробуем обновить по refresh-токену
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data.session?.user) return true;
  } catch {
    /* пойдём дальше — к переавторизации */
  }

  // 3) Переавторизация через Telegram (если это Telegram Mini App)
  const initData = (window as any)?.Telegram?.WebApp?.initData as string | undefined;
  if (initData) {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tg-auth`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ initData }),
        },
      );
      if (res.ok) {
        const { access_token, refresh_token } = await res.json();
        const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
        if (!error && data.session?.user) return true;
      }
    } catch {
      /* восстановить не вышло */
    }
  }

  return false;
}

/**
 * Обёртка для мест записи в БД: гарантирует сессию, а если восстановить не удалось —
 * показывает понятное сообщение и возвращает false.
 * Использование: `if (!(await requireSession())) return;`
 */
export async function requireSession(): Promise<boolean> {
  const ok = await ensureSession();
  if (!ok) {
    alert('Сессия потерялась. Закрой приложение полностью и открой заново, потом повтори действие.');
  }
  return ok;
}

/**
 * Заголовки для вызова Edge Functions напрямую через fetch.
 *
 * Пока часть функций (analyze-*, ai-chat) открыта, отсутствие сессии здесь НЕ ошибка:
 * запрос обязан проходить и без токена, иначе на переходном этапе мы уроним приём еды,
 * как это уже случилось 05.08.2026. Жёсткая проверка живёт в postWithAuth — она
 * срабатывает только когда сервер РЕАЛЬНО ответил 401, а не по нашей догадке.
 */
export async function functionHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    await ensureSession();
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  } catch {
    /* без токена тоже уходим — решение за сервером */
  }
  return headers;
}

/** Сообщение при неустранимой потере сессии. Формулировка нейтральная: в Telegram надо
 *  закрыть мини-апп, в вебе — обновить страницу, и одна фраза должна годиться для обоих. */
export const SESSION_LOST_MESSAGE =
  'Сессия потерялась. Перезапусти приложение и попробуй ещё раз.';

/**
 * POST в Edge Function с токеном сессии и ОДНИМ повтором при 401.
 *
 * Зачем повтор: потеря или протухание сессии в Telegram WebView — штатное явление,
 * а не сбой. Раньше это было незаметно, потому что функции пускали кого угодно.
 * Когда мы их закрываем, тот же самый случай превращается в отказ на ровном месте —
 * поэтому при 401 принудительно обновляем сессию и пробуем ещё раз.
 *
 * Если и после этого 401 — бросаем ПОНЯТНОЕ сообщение. Иначе человек увидит
 * «AI error 401» или «не удалось распознать» и не поймёт, что делать: ровно так
 * выглядела снаружи авария 05.08.2026.
 */
export async function postWithAuth(url: string, body: unknown): Promise<Response> {
  const send = async () =>
    fetch(url, { method: 'POST', headers: await functionHeaders(), body: JSON.stringify(body) });

  let res = await send();
  if (res.status === 401) {
    const restored = await ensureSession(true);
    if (restored) res = await send();
  }
  if (res.status === 401) throw new Error(SESSION_LOST_MESSAGE);
  return res;
}

/** То же самое, но по имени функции — чтобы не собирать адрес в каждом экране. */
export async function callFunction(name: string, body: unknown): Promise<Response> {
  return postWithAuth(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`, body);
}
